import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Cog6ToothIcon, 
  CheckIcon, 
  TruckIcon, 
  CreditCardIcon, 
  ClipboardDocumentCheckIcon,
  CakeIcon,
  ShieldCheckIcon,
  ServerIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  UsersIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { getInstitutions, updateInstitution, type Institution } from '@/api/institutionsApi';
import toast from 'react-hot-toast';

interface ModuleSettings {
  transport: boolean;
  rfid: boolean;
  attendance: boolean;
  meals: boolean;
}

interface InstitutionWithModules extends Institution {
  moduleStatus: ModuleSettings;
  expanded: boolean;
  saving: boolean;
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth()
  const [institutions, setInstitutions] = useState<InstitutionWithModules[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [globalSaving, setGlobalSaving] = useState(false)

  useEffect(() => {
    fetchInstitutions()
  }, [])

  const fetchInstitutions = async () => {
    try {
      setLoading(true)
      const institutionData = await getInstitutions()
      
      // Transform institutions to include module status and UI state
      const institutionsWithModules: InstitutionWithModules[] = institutionData.map(institution => {
        const settings = institution.settings || {}
        
        return {
          ...institution,
          moduleStatus: {
            transport: settings.features?.transport || settings.transport?.enabled || false,
            rfid: settings.features?.rfid ?? true,
            attendance: settings.features?.attendance ?? true,
            meals: settings.features?.meals || false
          },
          expanded: false,
          saving: false
        }
      })
      
      setInstitutions(institutionsWithModules)
      
    } catch (error: any) {
      console.error('Failed to fetch institutions:', error)
      toast.error('Failed to load institutions')
    } finally {
      setLoading(false)
    }
  }

  const toggleInstitutionExpanded = (institutionId: string) => {
    setInstitutions(prev => 
      prev.map(inst => 
        inst.id === institutionId 
          ? { ...inst, expanded: !inst.expanded }
          : inst
      )
    )
  }

  const handleModuleToggle = async (institutionId: string, module: keyof ModuleSettings, enabled: boolean) => {
    // Update UI immediately
    setInstitutions(prev => 
      prev.map(inst => 
        inst.id === institutionId 
          ? { 
              ...inst, 
              moduleStatus: { ...inst.moduleStatus, [module]: enabled },
              saving: true
            }
          : inst
      )
    )

    try {
      const institution = institutions.find(inst => inst.id === institutionId)
      if (!institution) throw new Error('Institution not found')

      // Prepare updated settings
      const updatedSettings = {
        ...institution.settings,
        features: {
          ...institution.settings?.features,
          [module]: enabled
        },
        transport: {
          ...institution.settings?.transport,
          enabled: module === 'transport' ? enabled : institution.settings?.transport?.enabled
        }
      }

      await updateInstitution(institutionId, {
        settings: updatedSettings
      } as any)

      toast.success(`${module.charAt(0).toUpperCase() + module.slice(1)} module ${enabled ? 'enabled' : 'disabled'} for ${institution.name}`)
      
      // Refresh institution data
      fetchInstitutions()
      
    } catch (error: any) {
      console.error('Failed to update module:', error)
      toast.error(error.response?.data?.message || 'Failed to update module')
      
      // Revert UI change on error
      setInstitutions(prev => 
        prev.map(inst => 
          inst.id === institutionId 
            ? { 
                ...inst, 
                moduleStatus: { ...inst.moduleStatus, [module]: !enabled },
                saving: false
              }
            : inst
        )
      )
    }
  }

  const expandAll = () => {
    setInstitutions(prev => prev.map(inst => ({ ...inst, expanded: true })))
  }

  const collapseAll = () => {
    setInstitutions(prev => prev.map(inst => ({ ...inst, expanded: false })))
  }

  // Filter institutions based on search query
  const filteredInstitutions = institutions.filter(institution =>
    institution.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    institution.settings?.city?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getModuleDescription = (module: string) => {
    switch (module) {
      case 'transport':
        return 'Enable school bus tracking, route planning, and student transport management'
      case 'rfid':
        return 'RFID card scanning, attendance tracking, and access control systems'
      case 'attendance':
        return 'Digital attendance recording, session tracking, and absence management'
      case 'meals':
        return 'Cafeteria management, meal planning, and dietary tracking'
      default:
        return 'Module configuration'
    }
  }

  const getModuleIcon = (module: string) => {
    switch (module) {
      case 'transport':
        return TruckIcon
      case 'rfid':
        return CreditCardIcon
      case 'attendance':
        return ClipboardDocumentCheckIcon
      case 'meals':
        return CakeIcon
      default:
        return Cog6ToothIcon
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="dashboard-card animate-pulse">
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Cog6ToothIcon className="h-8 w-8 text-blue-600 mr-3" />
            System Configuration
          </h1>
          <p className="text-gray-600 mt-1">
            Configure platform modules for each institution
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            onClick={expandAll}
            variant="secondary"
            size="sm"
          >
            Expand All
          </Button>
          <Button
            onClick={collapseAll}
            variant="secondary"
            size="sm"
          >
            Collapse All
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-input pl-10"
                placeholder="Search institutions by name or location..."
              />
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Showing {filteredInstitutions.length} of {institutions.length} institutions
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Institutions</p>
              <p className="text-2xl font-bold text-gray-900">{institutions.length}</p>
            </div>
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Transport Enabled</p>
              <p className="text-2xl font-bold text-green-600">
                {institutions.filter(inst => inst.moduleStatus.transport).length}
              </p>
            </div>
            <TruckIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">RFID Active</p>
              <p className="text-2xl font-bold text-purple-600">
                {institutions.filter(inst => inst.moduleStatus.rfid).length}
              </p>
            </div>
            <CreditCardIcon className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Schools</p>
              <p className="text-2xl font-bold text-orange-600">
                {institutions.filter(inst => inst.isActive).length}
              </p>
            </div>
            <CheckIcon className="h-8 w-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Institution List */}
      <div className="space-y-4">
        {filteredInstitutions.map((institution, index) => (
          <motion.div
            key={institution.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="dashboard-card"
          >
            {/* Institution Header */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleInstitutionExpanded(institution.id)}
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <BuildingOfficeIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{institution.name}</h3>
                  <p className="text-sm text-gray-500">
                    {institution.subscriptionPlan} • Created {new Date(institution.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* Module Status Badges */}
                <div className="flex items-center space-x-2">
                  {Object.entries(institution.moduleStatus).map(([module, enabled]) => (
                    <span
                      key={module}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {module === 'rfid' ? 'RFID' : module.charAt(0).toUpperCase() + module.slice(1)}
                    </span>
                  ))}
                </div>

                <div className="flex items-center">
                  {institution.expanded ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Expanded Configuration */}
            {institution.expanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="border-t border-gray-200"
              >
                <div className="p-6 space-y-6">
                  
                  {/* Platform Modules for this Institution */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                      <ServerIcon className="h-5 w-5 text-blue-600 mr-2" />
                      Platform Modules
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(institution.moduleStatus).map(([module, enabled]) => {
                        const IconComponent = getModuleIcon(module)
                        return (
                          <div key={module} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  enabled ? 'bg-blue-100' : 'bg-gray-100'
                                }`}>
                                  <IconComponent className={`h-5 w-5 ${
                                    enabled ? 'text-blue-600' : 'text-gray-400'
                                  }`} />
                                </div>
                                <div>
                                  <h5 className="text-sm font-semibold text-gray-900 capitalize">
                                    {module === 'rfid' ? 'RFID System' : `${module} Module`}
                                  </h5>
                                  <p className="text-xs text-gray-500">{getModuleDescription(module)}</p>
                                </div>
                              </div>
                              
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={enabled}
                                  onChange={(e) => handleModuleToggle(institution.id, module as keyof ModuleSettings, e.target.checked)}
                                  disabled={institution.saving}
                                  className="sr-only peer"
                                />
                                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${
                                  institution.saving ? 'opacity-50 cursor-not-allowed' : ''
                                }`}></div>
                              </label>
                            </div>

                            {/* Transport Special Features */}
                            {module === 'transport' && enabled && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                <div className="text-xs text-blue-600 space-y-1">
                                  <div>✓ Fleet & Route Management</div>
                                  <div>✓ Real-time GPS Tracking</div>
                                  <div>✓ Parent Notifications</div>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Institution Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Institution Details</h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Plan:</span>
                        <span className="ml-2 capitalize font-medium">{institution.subscriptionPlan}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Status:</span>
                        <span className={`ml-2 font-medium ${institution.isActive ? 'text-green-600' : 'text-red-600'}`}>
                          {institution.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Location:</span>
                        <span className="ml-2 font-medium">{institution.settings?.city || 'Not set'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Phone:</span>
                        <span className="ml-2 font-medium">{institution.settings?.phone || 'Not set'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredInstitutions.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {institutions.length === 0 ? 'No institutions found' : 'No matching institutions'}
            </h3>
            <p className="mt-2 text-gray-500">
              {institutions.length === 0 
                ? 'Create your first institution to begin system configuration.'
                : 'Try adjusting your search criteria.'
              }
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SettingsPage;